"use client";

import clsx from "clsx";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import type { AcfText } from "@/components/page-builder/types";
import WpImage from "@/components/ui/WpImage";
import type { MaybeMedia } from "@/lib/media";

export type HeroDeckCard = {
  image?: MaybeMedia;
  /** Category shown in the card head (Education, Industry, ...). */
  label?: AcfText;
  headline?: AcfText;
  text?: AcfText;
};

/** The two arrows share everything but the icon; the next one is the bright one while it can still move. */
const arrowClass =
  "flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-white/13 p-0 text-white transition-[background,opacity] duration-400 ease-[ease] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-das-green disabled:cursor-default disabled:opacity-28 [&_svg]:block";

/**
 * The port of hero-card-deck.js: the fanned deck of contributor cards in the
 * About hero. At rest the cards sit in one pile, each one turned a little
 * further than the last so the stack splays like a hand of cards. Bringing the
 * pointer onto the deck opens it out into a row: the card at the front squares
 * up, its neighbours slide aside, shrink slightly and fade back. Taking the
 * pointer away stacks it again. Walking the row is a deliberate act (the
 * arrows, the dots, a click on a card or the keyboard) and nothing else moves
 * the cards.
 *
 * Scrolling is deliberately no part of it. Hover is read from where the
 * pointer actually is rather than from mouseenter and mouseleave: those fire
 * when the deck slides out from under a resting cursor, which had the cards
 * springing open and shut as the page moved.
 *
 * The markup ships as a plain wrapping run of cards, so the section reads
 * before hydration; data-enhanced on the deck is what stacks them, and the
 * controls only appear once there is something to control.
 *
 * Placement is written as custom properties rather than inline transforms: the
 * deck's breakpoint utilities own the actual distances and angles (--card-w,
 * --deck-spread, --deck-rot and the rest), which is how the fan can be
 * tightened for tablets and reduced to a card-width shuffle on phones without
 * this component knowing the screen size. On phones a fan of full-width cards
 * has nowhere to go sideways, so the spread is just the card plus a gutter and
 * the tilt is halved.
 *
 * Reduced motion (data-calm) keeps the fan, since it is the design, but lands
 * on each state outright instead of travelling there.
 */
export default function HeroDeck({ cards }: { cards: HeroDeckCard[] }) {
  const deckRef = useRef<HTMLDivElement>(null);
  const hoveredRef = useRef(false);
  const [enhanced, setEnhanced] = useState(false);
  const [calm, setCalm] = useState(false);
  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [engaged, setEngaged] = useState(false);
  const last = cards.length - 1;

  /**
   * Open means the deck is showing its cards side by side; closed means it is
   * still a pile. Pointer devices open it by hovering; everything else has to
   * ask for a card, which is what engaged records.
   */
  const open = hovered || engaged;

  useEffect(() => {
    const deck = deckRef.current;

    if (!deck) {
      return;
    }

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const hoverable = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;

    // Enhancing only once mounted is what keeps the server markup the plain run.
    setEnhanced(true);
    setCalm(reduced);

    if (!hoverable || reduced) {
      return;
    }

    /**
     * Read the hover state from where the pointer actually is, and only ever
     * off the back of the pointer being moved. The deck passing under a still
     * cursor is the page moving, not the reader reaching for a card, so it
     * leaves the cards where they are.
     */
    const onMove = (event: MouseEvent) => {
      const rect = deck.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (inside === hoveredRef.current) {
        return;
      }

      hoveredRef.current = inside;
      setHovered(inside);

      // Taking the pointer away stacks the deck again, whatever was asked for
      // while it was open.
      if (!inside) {
        setEngaged(false);
      }
    };

    document.addEventListener("mousemove", onMove);

    return () => document.removeEventListener("mousemove", onMove);
  }, []);

  /** Bring a card to the front, opening the deck if it is still a pile. */
  const select = (next: number) => {
    setEngaged(true);
    setIndex(Math.min(last, Math.max(0, next)));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const key = event.key;

    if (key === "ArrowLeft" || key === "ArrowUp") {
      select(index - 1);
    } else if (key === "ArrowRight" || key === "ArrowDown") {
      select(index + 1);
    } else if (key === "Home") {
      select(0);
    } else if (key === "End") {
      select(last);
    } else {
      return;
    }

    event.preventDefault();
  };

  const placement = (position: number): CSSProperties | undefined => {
    if (!enhanced) {
      return undefined;
    }

    const offset = position - index;
    const distance = Math.abs(offset);
    const active = distance === 0;

    if (open) {
      return {
        "--offset": String(offset),
        "--tilt": "0",
        "--scale": active ? "1.03" : "0.94",
        "--fade": active ? "1" : "0.6",
        "--depth": active ? "10" : "5",
      } as CSSProperties;
    }

    // Fanned by distance rather than by position, so whichever card was last
    // brought forward is the one on top of the pile and the rest sit behind it
    // in the order they are reached.
    return {
      "--offset": "0",
      "--tilt": String(distance),
      "--scale": active ? "1" : String(Math.max(0.9, 1 - distance * 0.025)),
      "--fade": "1",
      "--depth": String(10 - distance),
    } as CSSProperties;
  };

  return (
    <div
      ref={deckRef}
      className="js-hero-deck group/deck relative [--card-h:580px] [--card-w:420px] [--deck-lift:100px] [--deck-rot:5deg] [--deck-spread:450px] [--media-h:330px] small-desktop:[--card-h:540px] small-desktop:[--card-w:372px] small-desktop:[--deck-spread:400px] small-desktop:[--media-h:296px] tablet:[--card-h:500px] tablet:[--card-w:320px] tablet:[--deck-lift:72px] tablet:[--deck-rot:4deg] tablet:[--deck-spread:344px] tablet:[--media-h:252px] mobile:[--card-h:470px] mobile:[--card-w:78vw] mobile:[--deck-lift:48px] mobile:[--deck-rot:2.5deg] mobile:[--deck-spread:calc(78vw+14px)] mobile:[--media-h:220px]"
      data-enhanced={enhanced}
      data-calm={enhanced && calm}
      data-open={enhanced && open}
      onKeyDown={enhanced ? onKeyDown : undefined}
    >
      <ul className="m-0 flex list-none flex-wrap justify-center gap-6 p-0 group-data-[enhanced=true]/deck:block group-data-[enhanced=true]/deck:h-[calc(var(--card-h)+var(--deck-lift))]">
        {cards.map((card, position) => {
          const number = String(position + 1).padStart(2, "0");
          const label = card.label || card.headline || number;

          return (
            <li
              key={position}
              className="w-(--card-w) max-w-full group-data-[enhanced=true]/deck:absolute group-data-[enhanced=true]/deck:top-[calc(var(--deck-lift)/2)] group-data-[enhanced=true]/deck:left-1/2 group-data-[enhanced=true]/deck:z-[var(--depth,1)] group-data-[enhanced=true]/deck:ml-[calc(var(--card-w)/-2)] group-data-[enhanced=true]/deck:origin-center group-data-[enhanced=true]/deck:[transform:translate3d(calc(var(--deck-spread)*var(--offset,0)),0,0)_rotate(calc(var(--deck-rot)*var(--tilt,0)))_scale(var(--scale,1))] group-data-[enhanced=true]/deck:opacity-[var(--fade,1)] group-data-[enhanced=true]/deck:will-change-[transform,opacity] group-data-[enhanced=true]/deck:group-data-[calm=false]/deck:[transition:transform_0.62s_cubic-bezier(0.16,1,0.3,1),opacity_0.62s_cubic-bezier(0.16,1,0.3,1)]"
              data-index={position}
              style={placement(position)}
            >
              <div className="relative flex h-full flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.45)] group-data-[enhanced=true]/deck:h-(--card-h)">
                {card.image && (
                  <div className="relative aspect-[42/33] w-full group-data-[enhanced=true]/deck:aspect-auto group-data-[enhanced=true]/deck:h-(--media-h) group-data-[enhanced=true]/deck:shrink-0">
                    <WpImage
                      media={card.image}
                      className="block h-full w-full object-cover"
                      alt=""
                      sizes="(max-width: 840px) 100vw, 840px"
                    />
                  </div>
                )}

                <div className="flex flex-1 flex-col gap-3 px-7 pt-6 pb-7 mobile:px-[22px] mobile:pt-5 mobile:pb-6">
                  <div className="flex items-center gap-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-blue text-[13px] font-bold text-white">
                      {number}
                    </span>

                    <span className="h-px flex-1 bg-brand-blue/8"></span>

                    {card.label && (
                      <span className="text-[11px] leading-[1.2] font-semibold tracking-[0.18em] text-das-green uppercase">
                        {card.label}
                      </span>
                    )}
                  </div>

                  {card.headline && (
                    <h3 className="m-0 text-[22px] leading-[1.15] font-semibold tracking-[-0.02em] text-brand-blue">
                      {card.headline}
                    </h3>
                  )}

                  {card.text && (
                    <p className="m-0 text-[13px] leading-[1.65] font-normal text-[#5a6a7a]">
                      {card.text}
                    </p>
                  )}
                </div>

                {/*
                 * The whole card is the hit area, as a real button so it is
                 * tabbable and answers to Enter and Space. It sits over the copy
                 * rather than wrapping it, which leaves the heading and paragraph
                 * in the reading order. Without the script it has nothing to do.
                 */}
                <button
                  type="button"
                  className="absolute top-0 left-0 h-full w-full cursor-pointer rounded-[inherit] border-0 bg-transparent bg-none p-0 group-data-[enhanced=false]/deck:hidden focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-das-green"
                  aria-label={`Bring ${label} to the front`}
                  // The front card is already at the front, so it is no longer a target.
                  aria-current={
                    enhanced
                      ? position === index
                        ? "true"
                        : "false"
                      : undefined
                  }
                  style={
                    enhanced
                      ? {
                          cursor:
                            position === index && open ? "default" : "pointer",
                        }
                      : undefined
                  }
                  onClick={() => select(position)}
                  // Tabbing onto a card brings it forward, so the keyboard walks
                  // the deck the same way the pointer does.
                  onFocus={() => select(position)}
                ></button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Hidden until the script is running: without it there is nothing to drive. */}
      <div className="hidden items-center justify-center gap-3 pb-4 group-data-[enhanced=true]/deck:flex">
        <button
          type="button"
          className={arrowClass}
          aria-label="Previous card"
          disabled={enhanced && index === 0}
          onClick={() => select(index - 1)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        <div className="flex h-9 items-center gap-[7px] rounded-[100px] bg-[rgba(18,18,20,0.62)] px-3.5 backdrop-blur-[14px]">
          {cards.map((card, position) => (
            <button
              key={position}
              type="button"
              className="group/dot flex cursor-pointer items-center border-0 bg-transparent bg-none px-0 py-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-das-green"
              data-index={position}
              data-active={enhanced && position === index}
              aria-label={`Go to card ${position + 1}`}
              aria-current={
                enhanced ? (position === index ? "true" : "false") : undefined
              }
              onClick={() => select(position)}
            >
              <span className="block h-2 w-2 rounded-[4px] bg-white opacity-36 transition-[width,opacity] duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] group-data-[active=true]/dot:w-[26px] group-data-[active=true]/dot:opacity-100 group-data-[calm=true]/deck:transition-none"></span>
            </button>
          ))}
        </div>

        <button
          type="button"
          className={clsx(
            arrowClass,
            "enabled:bg-white/92 enabled:text-brand-blue",
          )}
          aria-label="Next card"
          disabled={enhanced && index === last}
          onClick={() => select(index + 1)}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
